import { HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  GeoApiClient,
  type GeoLocation,
  type GeoSearchResultItem,
  LayerClient,
  type Locale,
} from '@smg-real-estate/geo-libs';

import { ApiTypes, logErrorToDatadogCustomMetric, logWarn } from '@/helpers/LogHelper';
import type {
  GeoActions,
  GeoGetters,
  GeoModule,
  GeoMutations,
  GeoState,
  PayloadAddGeoLocations,
  PayloadSetAutocompleteResults,
} from '@/store/types/geo-state';
import { captureError } from '@smg-real-estate/browser-rum';

const MAX_AUTOCOMPLETE_RESULT_COUNT = 20;
export const state = (): GeoState => ({
  geoLocations: [],
  unresolvableGeoIds: [],
  unresolvableUrlNames: {
    [HgSupportedLanguage.DE]: [],
    [HgSupportedLanguage.EN]: [],
    [HgSupportedLanguage.FR]: [],
    [HgSupportedLanguage.IT]: [],
  },
  autocompleteResults: [],
});

const showTypeLabel = (location: GeoLocation) =>
  ['canton', 'region', 'district'].includes(location.type);

export function renderGeoLocationName(
  location: GeoLocation,
  language: HgSupportedLanguage,
): string {
  const [first, ...rest] = location.names[language];

  return [
    showTypeLabel(location) ? location.typeLabel?.[language] : undefined,
    rest.length > 0 ? `${first} (${rest.join(', ')})` : first,
  ]
    .filter((x) => !!x)
    .join(' ');
}

export const getters: GeoGetters = {
  getGeoLocationByUrlName: (geoState) => (urlName, language) => {
    return geoState.geoLocations.find((geoLocation: GeoLocation) => {
      return (
        geoLocation.urlNames[language] === urlName ||
        (geoLocation.is24UrlNames && geoLocation.is24UrlNames[language] === urlName)
      );
    });
  },

  getGeoLocationsByUrlName: (geoState, getterAccess) => (urlNames, language) => {
    return urlNames
      .map((urlName) => getterAccess.getGeoLocationByUrlName(urlName, language))
      .filter((x) => !!x);
  },

  getGeoLocationById: (geoState) => (id) => {
    return geoState.geoLocations.find((geoLocation) => geoLocation.id === id);
  },

  getGeoLocationsById: (geoState, getterAccess) => (ids) => {
    return ids.map((id) => getterAccess.getGeoLocationById(id)).filter((x) => !!x);
  },

  getGeoLocationName: (geoState, getterAccess) => (id, language) => {
    const location = getterAccess.getGeoLocationById(id);

    if (location) {
      return renderGeoLocationName(location, language);
    }

    return undefined;
  },

  isUnresolvableUrlName: (geoState) => (urlName, language) => {
    return geoState.unresolvableUrlNames[language].indexOf(urlName) >= 0;
  },

  isUnresolvableId: (geoState) => (id) => {
    return geoState.unresolvableGeoIds.indexOf(id) >= 0;
  },

  /**
   * returns true when for every passed id there was a either a geo location resolved or resolved with an error.
   */
  allGeoLocationsByIdSettled: (geoState, getterAccess) => (ids) => {
    const settled = ids
      .map((id) => {
        return !!getterAccess.getGeoLocationById(id) || getterAccess.isUnresolvableId(id);
      })
      .filter((x) => x);

    return settled.length === ids.length;
  },

  /**
   * returns true when for every passed urlName there was a either a geo location resolved or resolved with an error.
   */
  allGeoLocationsByUrlNameSettled:
    (geoState, getterAccess) =>
    (urlNames, language): boolean => {
      const settled = urlNames
        .map((urlName) => {
          return (
            !!getterAccess.getGeoLocationByUrlName(urlName, language) ||
            getterAccess.isUnresolvableUrlName(urlName, language)
          );
        })
        .filter((x) => x);

      return settled.length === urlNames.length;
    },

  getAutocompleteResults(geoState) {
    return geoState.autocompleteResults ?? [];
  },
};

export const asPayloadSetAutocompleteResults = (
  results: GeoSearchResultItem[],
): PayloadSetAutocompleteResults => {
  return { type: 'setAutocompleteResults', results };
};

export const asPayloadAddGeoLocations = (geoLocations: GeoLocation[]): PayloadAddGeoLocations => {
  return { type: 'addGeoLocations', geoLocations };
};

export const mutations: GeoMutations = {
  addGeoLocations(geoState, { geoLocations }) {
    const uniqueGeoLocations = [...geoState.geoLocations, ...geoLocations].filter(
      (g1, i, array) => array.findIndex((g2) => g1.id === g2.id) === i,
    );

    geoState.geoLocations = uniqueGeoLocations.map(keepNecessaryFields);
  },

  addUnresolvableGeoIds(geoState, ids) {
    geoState.unresolvableGeoIds = [...geoState.unresolvableGeoIds, ...ids];
  },

  addUnresolvableUrlNames(geoState, { language, urlNames }) {
    geoState.unresolvableUrlNames[language] = [
      ...geoState.unresolvableUrlNames[language],
      ...urlNames,
    ];
  },

  setAutocompleteResults(geoState, { results }) {
    geoState.autocompleteResults = results;
  },
};

export const actions: GeoActions = {
  autocompleteLocations: async ({ commit }, { query, language, version }) => {
    try {
      const geoLocationsSearchResult = await getGeoApiClient().findGeoLocations(
        language,
        query,
        MAX_AUTOCOMPLETE_RESULT_COUNT,
        undefined,
        version,
      );
      commit(
        asPayloadAddGeoLocations(geoLocationsSearchResult.results.map((item) => item.geoLocation)),
      );
      commit(asPayloadSetAutocompleteResults(geoLocationsSearchResult.results));
    } catch (e) {
      captureError(e, { language, query });
      commit(asPayloadSetAutocompleteResults([]));
    }
  },

  /**
   * Function to resolve a list of geo-location-ids to corresponding GeeLocations and to attach them to the store.
   *
   * This function can be called from both server- and client-side, yet the path of action will differ.
   * If locations should be available in the store for the client, the server has to provide those locations
   * by executing this function.
   *
   * SSR:
   * When executed during server-side-rendering this method has access to the geo-layer attached to the lambda and
   * can resolve locations by doing a simple look-up by id.
   * However, in order to leverage this functionality the caller has to load and initialize the geo-data prior to
   * calling this function. `hg-geo-libs` provides this option via `LayerLoader.loadUnifiedGeoLayer` to do so.
   *
   * Client:
   * When this function gets executed by a client the geo-layer will not be available. The only options then are to
   * either find it in the store - in case it was added during SSR previously - or by requesting the search API.
   *
   * Execution path is as follow:
   * 1) Store: If a locationId is already in store no further actions have to be taken.
   * 2) GeoLayer: Do a look-up in the geo-layer if the locationId is known (SSR)
   * 3) SearchAPI: Request the search API to resolve locations.
   */
  resolveLocationsById: async ({ commit, state: geoState }, { geoLocationIds }): Promise<void> => {
    // 1) Store
    // Verify if some or all locations are already contained in the store.
    // If so, there is no need to search for them in the lambda-layer or via search API.
    geoLocationIds = filterNonExistingLocationIds(geoLocationIds, geoState.geoLocations);
    if (geoLocationIds.filter((location) => !!location).length === 0) {
      return;
    }

    // 2) Lambda-layer
    // Find locations within the geo data lambda layer (in case of SSR) and add them to the store.
    try {
      const locationsFoundInLayer: GeoLocation[] =
        await getLayerClient().getGeoLocationsById(geoLocationIds); // ✅ AWAIT dodat ovde

      commit(asPayloadAddGeoLocations(locationsFoundInLayer));
      geoLocationIds = filterNonExistingLocationIds(geoLocationIds, locationsFoundInLayer);
    } catch (e) {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      logWarn('Access to geo-data-layer failed', {}, e);
    }

    if (geoLocationIds.length === 0) {
      return;
    }

    // 3) Search API
    // For all locationIds that could neither be found in the store nor the layer the search-API gets requested.
    try {
      const locationsFoundByApi: GeoLocation[] =
        await getGeoApiClient().getGeoLocationsById(geoLocationIds);

      commit(asPayloadAddGeoLocations(locationsFoundByApi));

      const unresolvableIds = filterNonExistingLocationIds(geoLocationIds, locationsFoundByApi);
      if (unresolvableIds.length > 0) {
        commit('addUnresolvableGeoIds', unresolvableIds);
      }
    } catch (error) {
      captureError(error);
    }
  },

  resolveLocationsByUrlName: async ({ commit, state: geoState }, options) => {
    /* eslint-disable-next-line prefer-const */
    let { language, urlNames } = options as { language: string; urlNames: string[] };

    // 1) Store
    // Verify if some or all locations are already contained in the store.
    // If so, there is no need to search for them in the lambda-layer or via search API.
    urlNames = filterNonExistingUrlNames(language, urlNames, geoState.geoLocations);
    if (urlNames.length === 0) {
      return;
    }

    // 2) Lambda-layer
    // Find locations within the geo data lambda layer (in case of SSR) and add them to the store.
    try {
      const locationsFoundInLayer: GeoLocation[] = (
        await Promise.all(
          urlNames.map((urlName) =>
            getLayerClient().getGeoLocationByUrlName(urlName, language as Locale),
          ),
        )
      ).filter((x) => !!x) as GeoLocation[];

      commit(asPayloadAddGeoLocations(locationsFoundInLayer));
      urlNames = filterNonExistingUrlNames(language, urlNames, locationsFoundInLayer);
    } catch (e) {
      logWarn('Access to geo-data-layer failed', {}, e);
    }

    if (urlNames.length === 0) {
      return;
    }

    // 3) Search API
    // For all locationIds that could neither be found in the store nor the layer the search-API gets requested.
    const locationsFoundByApi: GeoLocation[] = (
      await Promise.all(
        urlNames.map(async (urlName) => {
          try {
            return await getGeoApiClient().getGeoLocationByUrlName(urlName, language as Locale);
          } catch (error) {
            logErrorToDatadogCustomMetric(ApiTypes.geo_api, { urlName, language }, error);
          }
        }),
      )
    ).filter((x) => !!x) as GeoLocation[];

    commit(asPayloadAddGeoLocations(locationsFoundByApi));

    const unresolvableUrlNames = filterNonExistingUrlNames(language, urlNames, locationsFoundByApi);
    if (unresolvableUrlNames.length > 0) {
      commit('addUnresolvableUrlNames', { urlNames: unresolvableUrlNames, language });
    }
  },
};

const filterNonExistingLocationIds = (locationIds: string[], geoLocations: GeoLocation[]) =>
  locationIds.filter(
    (locationId) => !geoLocations.find((geoLocation) => geoLocation.id === locationId),
  );

const filterNonExistingUrlNames = (lang: string, urlNames: string[], geoLocations: GeoLocation[]) =>
  urlNames.filter(
    (urlName) =>
      !geoLocations.find(
        (geoLocation) =>
          geoLocation.urlNames[lang as Locale] === urlName ||
          (geoLocation.is24UrlNames && geoLocation.is24UrlNames[lang as Locale] === urlName),
      ),
  );

function keepNecessaryFields({
  id,
  type,
  names,
  urlNames,
  is24UrlNames,
  parentPaths,
  children,
  typeLabel,
  preposition,
  wikidataId,
  newId,
}: GeoLocation): GeoLocation {
  return {
    id,
    type,
    names,
    urlNames,
    parentPaths,
    children,
    typeLabel,
    preposition,
    wikidataId,
    is24UrlNames,
    newId,
  };
}

let layerClient: LayerClient;
let geoApiClient: GeoApiClient;
const getLayerClient = (): LayerClient => {
  if (!layerClient) {
    layerClient = new LayerClient();
  }
  return layerClient;
};
const getGeoApiClient = (): GeoApiClient => {
  if (!geoApiClient) {
    const geoApiEndpoint = import.meta.env.VUE_GEO_API_ENDPOINT;
    if (!geoApiEndpoint) {
      throw new Error('VUE_GEO_API_ENDPOINT environment variable is not defined');
    }
    geoApiClient = new GeoApiClient(geoApiEndpoint);
  }
  return geoApiClient;
};

export default { namespaced: true, state, actions, mutations, getters } as GeoModule;
